import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
} from "recharts";
import api, { API } from "../lib/api";
import {
  Users, FileText, Wallet, AlertTriangle, ShieldAlert, Activity, ArrowUpRight, Radio,
} from "lucide-react";
import RiskMapCard from "../components/RiskMapCard";

const STATUS_COLORS = {
  flash_approved: "#114232",
  paid: "#114232",
  manual_review: "#d49a36",
  validating: "#d49a36",
  siu_review: "#c15337",
  escalated: "#c15337",
  rejected: "#a8472f",
  submitted: "#2c5b61",
  closed: "#5c6360",
};

function Metric({ label, value, hint, icon: Icon, mono = true, testid }) {
  return (
    <div className="metric-card" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5c6360]">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-[#5c6360]" strokeWidth={1.5} />}
      </div>
      <div className={`mt-3 text-3xl font-bold tracking-tight text-[#114232] ${mono ? "font-mono" : ""}`}>{value}</div>
      {hint && <div className="text-xs text-[#5c6360] mt-1">{hint}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: "badge-active", paid: "badge-paid", flash_approved: "badge-flash",
    success: "badge-success",
    pending: "badge-pending", validating: "badge-validating",
    cooling_off: "badge-cooling", manual_review: "badge-review",
    suspended: "badge-suspended", rejected: "badge-rejected", failed: "badge-failed",
  };
  return <span className={`badge-status ${map[status] || "badge-pending"}`}>{status?.replace(/_/g, " ")}</span>;
}

export default function Dashboard() {
  const { t } = useTranslation();
  const [m, setM] = useState(null);
  const [recent, setRecent] = useState(null);
  const [trends, setTrends] = useState(null);
  const [live, setLive] = useState({ connected: false, beats: 0, last: null });
  const sseRef = useRef(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setM(r.data));
    api.get("/admin/dashboard/recent-activity").then((r) => setRecent(r.data));
    api.get("/admin/dashboard/trends?days=30").then((r) => setTrends(r.data));
  }, []);

  // Real-time SSE: subscribe to /admin/stream and patch the metric tiles when
  // a heartbeat or notification frame arrives.
  useEffect(() => {
    const token = sessionStorage.getItem("nyk_token");
    const url = `${API}/admin/stream${token ? `?token=${encodeURIComponent(token)}` : ""}`;
    let es;
    try {
      es = new EventSource(url, { withCredentials: true });
    } catch (_) {
      return undefined;
    }
    sseRef.current = es;
    es.addEventListener("open", () => setLive((s) => ({ ...s, connected: true })));
    es.addEventListener("heartbeat", (ev) => {
      try {
        const h = JSON.parse(ev.data);
        setLive((s) => ({ connected: true, beats: s.beats + 1, last: h.ts }));
        // Patch metrics tiles live without a full refetch
        setM((cur) => (cur ? {
          ...cur,
          pending_claims: h.pending_claims ?? cur.pending_claims,
          fraud_queue_count: h.fraud_open ?? cur.fraud_queue_count,
        } : cur));
      } catch (_) { /* ignore */ }
    });
    es.addEventListener("notification", () => {
      // On every new notification, refresh recent activity (cheap, debounced
      // by SSE's own 5s cadence).
      api.get("/admin/dashboard/recent-activity").then((r) => setRecent(r.data));
    });
    es.onerror = () => setLive((s) => ({ ...s, connected: false }));
    return () => { es && es.close(); sseRef.current = null; };
  }, []);

  const claimsSeries = (trends?.claims_per_day || []).map((d) => ({
    day: d.day.slice(5), count: d.count,
  }));
  const premiumsSeries = (trends?.premiums_per_day || []).map((d) => ({
    day: d.day.slice(5), amount: d.amount,
  }));
  const fraudSeries = (trends?.fraud_per_day || []).map((d) => ({
    day: d.day.slice(5), count: d.count,
  }));
  const byStatus = (trends?.claims_by_status || []).map((s) => ({
    name: s.status.replace(/_/g, " "), value: s.count, key: s.status,
  }));

  return (
    <div className="px-8 pb-8 space-y-8" data-testid="dashboard-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#c15337]">{t("admin_dashboard.operations")}</div>
          <h1 className="font-heading text-4xl font-bold mt-1 text-[#114232]">{t("admin_dashboard.control_room")}</h1>
          <p className="text-sm text-[#5c6360] mt-1">{t("admin_dashboard.live_snapshot")}</p>
        </div>
        <div
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.16em] border ${
            live.connected
              ? "bg-[#114232]/6 text-[#114232] border-[#114232]/20"
              : "bg-[#5c6360]/8 text-[#5c6360] border-[#5c6360]/20"
          }`}
          data-testid="dashboard-sse-status"
          title={live.last ? `Last heartbeat ${new Date(live.last).toLocaleTimeString()}` : ""}
        >
          <Radio className={`w-3 h-3 ${live.connected ? "animate-pulse text-[#c15337]" : ""}`} />
          {live.connected ? `Live · ${live.beats} beats` : "Reconnecting…"}
        </div>
      </header>

      <section className="dashboard-grid">
        <Metric testid="metric-total-merchants" label={t("admin_dashboard.total_merchants")} icon={Users}
                value={m?.total_merchants ?? "—"} hint={t("admin_dashboard.all_registered")} />
        <Metric testid="metric-active-policies" label={t("admin_dashboard.active_policies")} icon={FileText}
                value={m?.active_policies ?? "—"} hint={t("admin_dashboard.including_cooling")} />
        <Metric testid="metric-weekly-premium" label={t("admin_dashboard.weekly_premium")} icon={Wallet}
                value={m ? `KES ${m.weekly_premium_collected.toLocaleString()}` : "—"}
                hint={t("admin_dashboard.settled_7d")} />
        <Metric testid="metric-cyber-score" label={t("admin_dashboard.avg_cyberscore")} icon={Activity}
                value={m?.average_cyber_score ?? "—"} hint={t("admin_dashboard.across_all")} />
        <Metric testid="metric-total-claims" label={t("admin_dashboard.total_claims")} icon={AlertTriangle}
                value={m?.total_claims ?? "—"} hint={t("admin_dashboard.all_time")} />
        <Metric testid="metric-pending-claims" label={t("admin_dashboard.pending_claims")}
                value={m?.pending_claims ?? "—"} hint={t("admin_dashboard.awaiting_decision")} />
        <Metric testid="metric-fraud-queue" label={t("admin_dashboard.fraud_queue")} icon={ShieldAlert}
                value={m?.fraud_queue_count ?? "—"} hint={t("admin_dashboard.open_siu")} />
        <Metric testid="metric-claims-ratio" label={t("admin_dashboard.claims_ratio")}
                value={m ? (m.claims_ratio * 100).toFixed(1) + "%" : "—"}
                hint={t("admin_dashboard.paid_total")} />
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <a href="/loyalty" className="metric-card group block" data-testid="metric-rebate-eligible">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5c6360]">{t("admin_dashboard.rebate_eligible")}</div>
            <span className="text-[#d49a36] group-hover:scale-110 transition-transform">✦</span>
          </div>
          <div className="mt-3 text-3xl font-bold tracking-tight font-mono text-[#114232]">{m?.merchants_eligible_for_rebate ?? "—"}</div>
          <div className="text-xs text-[#5c6360] mt-1">{t("admin_dashboard.rebate_eligible_hint")}</div>
        </a>
        <div className="metric-card" data-testid="metric-flash-approved" data-flash="true">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5c6360]">{t("admin_dashboard.flash_approved")}</div>
          <div className="mt-3 text-3xl font-bold tracking-tight font-mono text-[#114232]">{m?.flash_approved_claims ?? "—"}</div>
          <div className="text-xs text-[#5c6360] mt-1">{t("admin_dashboard.flash_approved_hint")}</div>
        </div>
        <div className="metric-card" data-testid="metric-suspended">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5c6360]">{t("admin_dashboard.suspended_policies")}</div>
          <div className="mt-3 text-3xl font-bold tracking-tight font-mono text-[#114232]">{m?.suspended_policies ?? "—"}</div>
          <div className="text-xs text-[#5c6360] mt-1">{t("admin_dashboard.suspended_hint")}</div>
        </div>
      </section>

      {/* Trend charts */}
      <section className="grid grid-cols-1 xl:grid-cols-3 gap-4" data-testid="dashboard-charts">
        <div className="metric-card xl:col-span-2" data-testid="chart-claims">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading text-base font-semibold text-[#114232]">{t("admin_dashboard.claims_trend")}</div>
            <span className="text-xs text-[#5c6360]">last 30 days</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={claimsSeries} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="clGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#c15337" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#c15337" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(17,66,50,0.08)" vertical={false} />
              <XAxis dataKey="day" stroke="#5c6360" fontSize={10} />
              <YAxis stroke="#5c6360" fontSize={10} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(17,66,50,0.15)", borderRadius: 6, fontSize: 12 }} />
              <Area type="monotone" dataKey="count" stroke="#c15337" strokeWidth={2} fill="url(#clGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="metric-card" data-testid="chart-claims-by-status">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading text-base font-semibold text-[#114232]">{t("admin_dashboard.claims_by_status")}</div>
          </div>
          {byStatus.length === 0 ? (
            <div className="h-[200px] grid place-items-center text-xs text-[#5c6360]">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={byStatus} dataKey="value" cx="50%" cy="50%" innerRadius={42} outerRadius={72} paddingAngle={1.5}>
                  {byStatus.map((d) => (
                    <Cell key={d.key} fill={STATUS_COLORS[d.key] || "#5c6360"} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(17,66,50,0.15)", borderRadius: 6, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="metric-card xl:col-span-2" data-testid="chart-premiums">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading text-base font-semibold text-[#114232]">{t("admin_dashboard.premium_trend")}</div>
            <span className="text-xs text-[#5c6360]">KES — last 30 days</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={premiumsSeries} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(17,66,50,0.08)" vertical={false} />
              <XAxis dataKey="day" stroke="#5c6360" fontSize={10} />
              <YAxis stroke="#5c6360" fontSize={10} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(17,66,50,0.15)", borderRadius: 6, fontSize: 12 }} />
              <Bar dataKey="amount" fill="#114232" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="metric-card" data-testid="chart-fraud">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading text-base font-semibold text-[#114232]">{t("admin_dashboard.fraud_trend")}</div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={fraudSeries} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(17,66,50,0.08)" vertical={false} />
              <XAxis dataKey="day" stroke="#5c6360" fontSize={10} />
              <YAxis stroke="#5c6360" fontSize={10} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(17,66,50,0.15)", borderRadius: 6, fontSize: 12 }} />
              <Line type="monotone" dataKey="count" stroke="#d49a36" strokeWidth={2} dot={{ r: 3, fill: "#d49a36" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white border border-[#114232]/10 rounded-lg" data-testid="recent-claims-card">
          <div className="px-5 py-4 border-b border-[#114232]/10 flex items-center justify-between">
            <div className="font-heading text-base font-semibold text-[#114232]">{t("admin_dashboard.recent_claims")}</div>
            <a href="/claims" className="text-xs text-[#c15337] inline-flex items-center gap-1 hover:underline">
              {t("admin_dashboard.view_all")} <ArrowUpRight className="w-3 h-3" />
            </a>
          </div>
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-[#5c6360]">
              <tr>
                <th className="text-left font-semibold py-2 px-5">{t("admin_dashboard.claim_number")}</th>
                <th className="text-left font-semibold py-2 px-5">{t("admin_dashboard.type")}</th>
                <th className="text-left font-semibold py-2 px-5">{t("admin_dashboard.amount")}</th>
                <th className="text-left font-semibold py-2 px-5">{t("admin_dashboard.status")}</th>
              </tr>
            </thead>
            <tbody>
              {(recent?.recent_claims || []).map((c) => (
                <tr key={c.id} className="border-t border-[#114232]/10 table-row-hover">
                  <td className="py-2 px-5 font-mono text-xs">{c.claim_number}</td>
                  <td className="py-2 px-5">{c.claim_type.replace(/_/g, " ")}</td>
                  <td className="py-2 px-5 font-mono">KES {c.amount_claimed.toLocaleString()}</td>
                  <td className="py-2 px-5"><StatusBadge status={c.status} /></td>
                </tr>
              ))}
              {recent?.recent_claims?.length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-[#5c6360]">No claims yet</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-[#114232]/10 rounded-lg" data-testid="recent-premiums-card">
          <div className="px-5 py-4 border-b border-[#114232]/10 flex items-center justify-between">
            <div className="font-heading text-base font-semibold text-[#114232]">{t("admin_dashboard.recent_premiums")}</div>
            <a href="/ledger" className="text-xs text-[#c15337] inline-flex items-center gap-1 hover:underline">
              {t("admin_dashboard.view_ledger")} <ArrowUpRight className="w-3 h-3" />
            </a>
          </div>
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-[#5c6360]">
              <tr>
                <th className="text-left font-semibold py-2 px-5">{t("admin_dashboard.reference")}</th>
                <th className="text-left font-semibold py-2 px-5">{t("admin_dashboard.provider")}</th>
                <th className="text-left font-semibold py-2 px-5">{t("admin_dashboard.amount")}</th>
                <th className="text-left font-semibold py-2 px-5">{t("admin_dashboard.status")}</th>
              </tr>
            </thead>
            <tbody>
              {(recent?.recent_premiums || []).map((p) => (
                <tr key={p.id} className="border-t border-[#114232]/10 table-row-hover">
                  <td className="py-2 px-5 font-mono text-xs">{p.reference}</td>
                  <td className="py-2 px-5 capitalize">{p.provider}</td>
                  <td className="py-2 px-5 font-mono">KES {p.amount.toLocaleString()}</td>
                  <td className="py-2 px-5"><StatusBadge status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <RiskMapCard />
    </div>
  );
}
